import { Maybe, Nothing } from 'purify-ts/Maybe';
import { compare, Order } from './compare';
import * as itertools from './itertools';
/**
 * Wrap the given object in an array, if it is not an array already.
 *
 * @param obj The object to potentially wrap.
 * @returns An array either containing or equal to `obj`.
 */
export function ensureArray(obj) {
    if (Array.isArray(obj)) {
        return obj;
    }
    return [obj];
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function sumMap(arr, mapper, initial = 0) {
    let res = initial;
    for (const el of arr) {
        res += mapper(el);
    }
    return res;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function last(arr) {
    if (arr.length === 0) {
        return undefined;
    }
    return arr[arr.length - 1];
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function innerFindMaxMin(getValue, arr, wantedOrder) {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    let curMax = null;
    let idx = 0;
    for (const item of arr) {
        const value = getValue(item, idx);
        idx += 1;
        if (curMax == null) {
            curMax = { item, value };
        }
        else if (compare(value, curMax.value) === wantedOrder) {
            curMax.item = item;
            curMax.value = value;
        }
    }
    return Maybe.fromNullable(curMax?.item);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function findMax(getValue, arr) {
    switch (arguments.length) {
        case 0:
            return findMax;
        case 1:
            return (realArr) => innerFindMaxMin(getValue, realArr, Order.GT);
        default:
            return innerFindMaxMin(getValue, arr, Order.GT);
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function findMin(getValue, arr) {
    switch (arguments.length) {
        case 0:
            return findMin;
        case 1:
            return (realArr) => innerFindMaxMin(getValue, realArr, Order.LT);
        default:
            return innerFindMaxMin(getValue, arr, Order.LT);
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function findMaybeIndex(predicate, arr) {
    let i = 0;
    for (const el of arr) {
        if (predicate(el, i)) {
            return Maybe.of(i);
        }
        i++;
    }
    return Maybe.empty();
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function findMaybeInner(predicate, arr) {
    let i = 0;
    for (const el of arr) {
        if (predicate(el, i)) {
            return Maybe.of(el);
        }
        i++;
    }
    return Nothing;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function findMaybe(predicate, arr) {
    switch (arguments.length) {
        case 0:
            return findMaybe;
        case 1:
            return (realArr) => findMaybeInner(predicate, realArr);
        default:
            return findMaybeInner(predicate, arr);
    }
}
/**
 * Default random number generator for `shuffle`.
 *
 * @param max The maximum number (exclusive) to generate.
 * @returns A random number in the interval [0, max).
 */
function defaultGetRandInt(max) {
    return Math.floor(Math.random() * max);
}
/**
 * Shuffle an array in place.
 *
 * @param arr The array to be shuffled.
 * @param getRandInt An optional random number generator. If not given `Math.random` is used.
 *     This function should generate a number in the interval [0, max).
 */
export function shuffle(arr, getRandInt = defaultGetRandInt) {
    for (let i = arr.length - 1; i > 0; i--) {
        const j = getRandInt(i + 1);
        const tmp = arr[j];
        arr[j] = arr[i];
        arr[i] = tmp;
    }
}
/**
 * Takes a predicate and a list of values and returns a a tuple (2-item array),
 * with each item containing the subset of the list that matches the predicate
 * and the complement of the predicate respectively.
 *
 * @param arr The iterable to partition.
 * @param predicate A predicate to determine which side the element belongs to.
 * @returns A tuple with two arrays, the first array containing values for which `predicate`
 * returned false, the second values for which `predicate` returned true.
 *
 * Inspired by the Ramda function of the same name.
 * @see https://ramdajs.com/docs/#partition.
 * @example
 *
 * const isNegative: (n: number) => boolean = n => n < 0
 * const numbers = [1, 2, -4, -7, 4, 22]
 * partition(isNegative, numbers)
 * // => [ [-4, -7], [1, 2, 4, 22] ]
 */
export function partition(arr, predicate) {
    const partitioned = [[], []];
    for (const val of arr) {
        const partitionIndex = predicate(val) ? 0 : 1;
        partitioned[partitionIndex].push(val);
    }
    return partitioned;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function partitionMap(arr, partitionMapper) {
    return itertools.reduce(arr, (res, el, idx) => {
        partitionMapper(el, idx).caseOf({
            Left: val => res[0].push(val),
            Right: val => res[1].push(val),
        });
        return res;
    }, [[], []]);
}
/**
 * Check if a given object is null.
 *
 * @param obj The object to check for null.
 * @returns If `obj` is null.
 */
function isObjNull(obj) {
    return obj == null;
}
/**
 * Takes a generic array and trims away from the head and tail the `nullish`
 * elements. To determine whether an element is nullish, it uses a `isNull`
 * function that by default checks whether the value == null. A custom
 * function can be provided.
 *
 * @param arr The array to trim.
 * @param isNull The validator checking if an entry is nullish.
 * @returns The trimmed array.
 * @example
 *
 * const isNull = v => v.trim() === '';
 * const arr = ['', '   ', 'Hi', ' ', 'World', '', '         ', '\n'];
 * trim(arr, isNull);
 * // => [ 'Hi', ' ', 'World'];
 */
export function trim(arr, isNull = isObjNull) {
    let res = arr;
    let start = 0;
    const arrLen = arr.length;
    while (start < arrLen && isNull(arr[start])) {
        start++;
    }
    let end = res.length - 1;
    while (end >= 0 && isNull(arr[end])) {
        end--;
    }
    res = res.slice(start, end + 1);
    return res;
}
/**
 * Extends the array up to the desired length, by filling it with a provided element.
 *
 * @param array The original array to be extended.
 * @param finalLength Final length of the array.
 * @param filler Function that obtains the element that will be added to pad the array until it reaches the correct length.
 * @returns The array itself if the final length is less than the current one or the extended array.
 */
export function pad(array, finalLength, filler) {
    if (finalLength <= array.length) {
        return array;
    }
    return array.concat(Array.from({ length: finalLength - array.length }, (_, i) => filler(i + array.length)));
}
const NO_VALUE = Symbol('NO_VALUE');
/**
 * Generator that yields all elements of an iterable except the last.
 *
 * @param it The iterable of which the last element should be stripped.
 * @yields All elements from `it` except the last one.
 */
export function* butLast(it) {
    // We don't use a Maybe here to reduce allocations.
    let l = NO_VALUE;
    for (const el of it) {
        if (l === NO_VALUE) {
            l = el;
        }
        else {
            yield l;
            l = el;
        }
    }
}
/**
 * Flatten an array one level.
 *
 * @param arr The iterable to flatten one level.
 * @returns An array containing the elements of the inner arrays of `arr`.
 */
export function flat1(arr) {
    const res = [];
    for (const subArr of arr) {
        for (const el of subArr) {
            res.push(el);
        }
    }
    return res;
}
/** @inheritdoc */
export function zip(...lists) {
    if (lists.length === 0) {
        return [];
    }
    const acc = [];
    const end = Math.min(...lists.map(l => l.length));
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const getter = (i) => (l) => l[i];
    for (let i = 0; i < end; i++) {
        acc.push(lists.map(getter(i)));
    }
    return acc;
}
/**
 * Apply a function to each pair of the result of zipping two lists.
 *
 * @param f The function to apply.
 * @param a The first list to zip up.
 * @param b The second list to zip up.
 * @returns A list of results of applying `f` to the list of zipped pairs of `a` and `b`.
 */
export function zipWith(f, a, b) {
    return zip(a, b).map(([x, y]) => f(x, y));
}
/**
 * Create a pair of lists from a list of pairs.
 *
 * The first list contains all the first elements of the pairs, while the second list
 * contains all the second elements of the pairs.
 *
 * The resulting lists have the length of the shortest input list.
 *
 * @param arr The input list of pairs.
 * @returns A pair of lists.
 */
export function unzip2(arr) {
    const base = [[], []];
    return arr.reduce((acc, item) => {
        acc[0].push(item[0]);
        acc[1].push(item[1]);
        return acc;
    }, base);
}
/**
 * Map over an array, allowing to provide a custom map function for the first or last element.
 *
 * @param arr The array to be mapped.
 * @param fun The general mapping function.
 * @param funFirst A custom mapping function for the first element.
 * @param funLast A custom mapping function for the last element.
 * @returns The mapped array.
 */
export function mapCustom(arr, fun, funFirst = fun, funLast = fun) {
    const max = arr.length - 1;
    return arr.map((v, i) => {
        if (i === 0) {
            return funFirst(v, i);
        }
        else if (i === max) {
            return funLast(v, i);
        }
        else {
            return fun(v, i);
        }
    });
}
